import { useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    buildStageButton,
    renderStageFooter,
    StageHost,
    useStageData,
    type StageProps,
    type StageValue,
} from "./PickerPlaygroundStage.lib";
import { resolveTrigger } from "./PickerPlaygroundOptionControls";
import { CustomValueStage } from "./PickerPlaygroundStageCustomValue";
import {
    usePickerPlaygroundPagedItems,
    type PickerPlaygroundListData,
} from "./usePickerPlaygroundPagedItems";

const accountCopy = {
    placeholder: "Select account",
    searchPlaceholder: "Search accounts",
    footerLabel: "Add account",
    loadingLabel: "Loading accounts",
    errorLabel: "Accounts could not be loaded",
};

const companyCopy = {
    placeholder: "Select company",
    searchPlaceholder: "Search companies",
    footerLabel: "Add company",
    loadingLabel: "Loading companies",
    errorLabel: "Companies could not be loaded",
};

/** Selecting stage: every toggle flows through the data-driven props. */
const ListStage = ({
    features,
    forcedState,
    onValueChange,
    onFooterSelect,
    onPagingChange,
}: StageProps) => {
    const [singleValue, setSingleValue] = useState<string | null>(null);
    const [multipleValues, setMultipleValues] = useState<string[]>([]);
    const { items, sections, flyoutDefaults } = useStageData(
        features,
        onFooterSelect,
    );
    const pagedData = usePickerPlaygroundPagedItems({
        features,
        onPagingChange,
    });
    const trigger = resolveTrigger(features);
    const listData: PickerPlaygroundListData = features.isPaged
        ? pagedData
        : { items, sections, status: features.status };
    const { searchPlaceholder, footerLabel, ...copy } = features.isPaged
        ? companyCopy
        : accountCopy;
    // A paged source searches on the server, so the root never filters.
    const isRootFiltering = !features.isPaged;

    const sharedProps = {
        ...listData,
        ...copy,
        nested: features.nested,
        defaultExpandAll: features.isTree && features.isExpandedByDefault,
        selectableParents: features.isTree
            ? features.hasSelectableParents
            : undefined,
        flyoutDefaults,
        flyoutQueryScope: features.flyoutQueryScope,
        flyoutAutoOpen: features.flyoutAutoOpen,
        chevron: features.chevron,
        clearable: features.isClearable,
        disabled: features.isDisabled,
        emptyBehavior: features.emptyBehavior,
        selectionSummary: { mode: features.summaryMode },
        renderFooter: features.hasFooter
            ? renderStageFooter(footerLabel, onFooterSelect)
            : undefined,
        "data-forced-state": forcedState,
    };
    const modeProps = features.isMultiple
        ? {
              multiple: true,
              parentSelection: features.parentSelection,
              value: multipleValues,
              onValueChange: (next: StageValue) => {
                  const nextValues = Array.isArray(next) ? next : [];
                  setMultipleValues(nextValues);
                  onValueChange(nextValues);
              },
          }
        : {
              value: singleValue,
              onValueChange: (next: StageValue) => {
                  const nextValue = Array.isArray(next) ? null : next;
                  setSingleValue(nextValue);
                  onValueChange(nextValue);
              },
          };

    let rootNode;
    if (trigger === "cellInput") {
        rootNode = (
            <Picker.Select<string, boolean>
                {...sharedProps}
                {...modeProps}
                trigger="cellInput"
                filterable={isRootFiltering && features.isFilterable}
            />
        );
    } else if (trigger === "input") {
        rootNode = (
            <Picker.Select<string, boolean>
                {...sharedProps}
                {...modeProps}
                trigger="input"
                filterable={isRootFiltering && features.isFilterable}
            />
        );
    } else if (trigger === "cell") {
        rootNode = (
            <Picker.Select<string, boolean>
                {...sharedProps}
                {...modeProps}
                trigger="cell"
                searchable={features.isSearchable}
                searchPlaceholder={searchPlaceholder}
                filterable={isRootFiltering}
            />
        );
    } else {
        rootNode = (
            <Picker.Select<string, boolean>
                {...sharedProps}
                {...modeProps}
                trigger={buildStageButton(features.buttonVariant)}
                searchable={features.isSearchable}
                searchPlaceholder={searchPlaceholder}
                filterable={isRootFiltering}
            />
        );
    }

    return <StageHost trigger={trigger}>{rootNode}</StageHost>;
};

/**
 * The live root the sandbox toggles drive: `Picker.Select`, selecting or
 * with a custom value, fed through its data-driven props so the stage
 * exercises the same paths product code will.
 */
export const PickerPlaygroundStage = (props: StageProps) => {
    const { features } = props;
    const trigger = resolveTrigger(features);
    if (
        features.allowsCustomValue &&
        (trigger === "input" || trigger === "cellInput")
    ) {
        return (
            <StageHost trigger={trigger}>
                <CustomValueStage {...props} />
            </StageHost>
        );
    }
    return <ListStage {...props} />;
};
